import { lazy } from 'react'
import type { ComponentType, RefObject } from 'react'
import type { Features, PatientUser, Person } from '~/shared/types'
import { FLAG_CRM_CARE_KEY_SAVINGS_TAB, FLAG_CRM_MEDICATIONS_TAB } from './constants'

// `lazy` here breaks the import cycle between the nav and the views it renders; the build
// inlines dynamic imports, so it is not a code-splitting boundary.
const PatientCategorizedTodos = lazy(() => import('~/features/member-profile/overview/todos'))
const PatientDetailTimeline = lazy(() => import('~/features/member-profile/overview/timeline'))
const ProgramTab = lazy(() => import('~/features/member-profile/overview/programs'))
const PatientDetailForms = lazy(() => import('~/features/member-profile/overview/forms'))
const PatientDetailOnboarding = lazy(() => import('~/features/member-profile/overview/onboarding'))
const PatientDetailAppointments = lazy(
  () => import('~/features/member-profile/clinical/appointments'),
)
const CarePlans = lazy(() => import('~/features/member-profile/clinical/care-plans'))
const ZusAggregatedProfile = lazy(() => import('~/features/member-profile/clinical/zus-profile'))
const CareGaps = lazy(() => import('~/features/member-profile/clinical/care-gaps'))
const Medications = lazy(() => import('~/features/member-profile/clinical/medications'))
const Navigation = lazy(() => import('~/features/member-profile/care-navigation/navigation'))
const ProviderSearch = lazy(
  () => import('~/features/member-profile/care-navigation/provider-search'),
)
const PriorAuthorization = lazy(
  () => import('~/features/member-profile/care-navigation/prior-auth'),
)
const EditPatient = lazy(() => import('~/features/member-profile/profile/profile-edit'))
const EditPerson = lazy(() =>
  import('~/features/member-profile/profile/profile-edit').then(m => ({ default: m.EditPerson })),
)
const PatientDetailMeta = lazy(() => import('~/features/member-profile/profile/device'))
// The Health Plan tabs each gate their own Waffle switch and pick their placeholder internally,
// so each is a single component that serves both nav modes.
const BenefitDocuments = lazy(
  () => import('~/features/member-profile/health-plan/benefit-documents'),
)
const Claims = lazy(() => import('~/features/member-profile/health-plan/claims'))
const Accumulators = lazy(() => import('~/features/member-profile/health-plan/accumulators'))
const CareKeySavings = lazy(() => import('~/features/member-profile/health-plan/care-key-savings'))
const AdditionalBenefits = lazy(
  () => import('~/features/member-profile/health-plan/additional-benefits'),
)

export type NavMode = 'patient' | 'person'

export type NavCategoryId = 'common' | 'clinical' | 'navigation' | 'healthPlan' | 'profile'

/** What the nav hands every detail view; a view declares the subset it reads. */
interface NavViewProps {
  patient: PatientUser
  scrollRef: RefObject<HTMLDivElement | null>
  scrollEventTarget: EventTarget
}

/** A view's person-mode variant, rendered with the raw Person record. */
interface PersonNavViewProps extends Omit<NavViewProps, 'patient'> {
  patient: Person
}

export interface NavItem {
  label: string
  to: string
  component: ComponentType<NavViewProps>
  // One-line summary surfaced as the sub-tab pill's hover tooltip.
  description?: string
  feature?: Features
  featureFlag?: string
  featureSwitch?: string
  // Person-mode overrides: skip the item, or render this variant with the raw Person record
  // instead of the synthetic PatientUser.
  hideInPersonMode?: boolean
  personComponent?: ComponentType<PersonNavViewProps>
}

export interface NavCategory {
  id: NavCategoryId
  label: string
  items: NavItem[]
  // Person-mode: hide the entire category (e.g., Health Plan has no equivalent for non-users).
  hideInPersonMode?: boolean
}

export const NAV_TREE: NavCategory[] = [
  {
    id: 'common',
    label: 'Overview',
    items: [
      {
        label: 'To Dos',
        to: '/todos',
        component: PatientCategorizedTodos,
        description: 'Cases, tasks and care plans for this member',
        feature: 'tasks',
      },
      {
        label: 'Timeline',
        to: '/timeline',
        component: PatientDetailTimeline,
        description: "The member's journey across coverage, visits, care and contact",
        hideInPersonMode: true,
      },
      {
        label: 'Programs',
        to: '/programs',
        component: ProgramTab,
        description: 'Program enrollment and clinical status',
        feature: 'programs',
      },
      {
        label: 'Forms',
        to: '/forms',
        component: PatientDetailForms,
        description: 'Assigned forms and submissions',
        feature: 'forms',
        hideInPersonMode: true,
      },
      {
        label: 'Onboarding',
        to: '/onboarding',
        component: PatientDetailOnboarding,
        description: 'Member onboarding progress',
        feature: 'onboarding',
        hideInPersonMode: true,
      },
    ],
  },
  {
    id: 'clinical',
    label: 'Clinical',
    items: [
      {
        label: 'Appointments',
        to: '/appointments',
        component: PatientDetailAppointments,
        description: 'Upcoming and past visits',
        feature: 'appointment_booking',
        hideInPersonMode: true,
      },
      {
        label: 'Care Plans',
        to: '/careplans',
        component: CarePlans,
        description: 'Active care plans and tasks',
        feature: 'care_plans',
        hideInPersonMode: true,
      },
      {
        label: 'ZAP',
        to: '/zap',
        component: ZusAggregatedProfile,
        description: 'Zus Aggregated Profile',
      },
      {
        label: 'Care Gaps',
        to: '/care-gaps',
        component: CareGaps,
        description: 'Open quality-measure care gaps and their cases',
        // No `hideInPersonMode`, unlike its Clinical siblings: a MeasureReport hangs off
        // the Person, not a patient account, so a Person who never signed up can still
        // have gaps worth working. Same reasoning as ZAP.
      },
      {
        label: 'Medications',
        to: '/medications',
        component: Medications,
        description: 'Elation medications and pharmacies',
        featureFlag: FLAG_CRM_MEDICATIONS_TAB,
        // No `hideInPersonMode`: the rows hang off the Person, like Care Gaps.
      },
    ],
  },
  {
    id: 'navigation',
    label: 'Navigation',
    items: [
      {
        label: 'Navigation',
        to: '/navigation',
        component: Navigation,
        description: 'Network navigation — referrals & Care Keys',
        feature: 'waiver',
      },
      {
        label: 'Provider Search',
        to: '/providersearch',
        component: ProviderSearch,
        description: 'Find in-network providers, facilities & procedures',
        feature: 'waiver',
      },
      {
        label: 'Prior Authorization',
        to: '/priorauthorization',
        component: PriorAuthorization,
        description: 'Prior authorization list',
        feature: 'prior_auth',
      },
    ],
  },
  {
    id: 'healthPlan',
    label: 'Health Plan',
    items: [
      {
        label: 'Benefit Documents',
        to: '/benefit-documents',
        component: BenefitDocuments,
        description: 'Plan benefit documents',
      },
      {
        label: 'Accumulators',
        to: '/accumulators',
        component: Accumulators,
        description: 'Deductible and out-of-pocket tracking',
      },
      {
        label: 'Claims',
        to: '/claims',
        component: Claims,
        description: 'Claims history',
      },
      {
        label: 'Additional Benefits',
        to: '/additional-benefits',
        component: AdditionalBenefits,
        description: 'Point solutions and partner benefits',
      },
      {
        label: 'Care Key Savings',
        to: '/care-key-savings',
        component: CareKeySavings,
        description: 'Realized and missed Care Key savings',
        // Per-user flag, not a switch: the tab exists to be assessed, so a few stakeholders get
        // it before anyone else. Un-flagged users see no entry at all rather than a placeholder.
        featureFlag: FLAG_CRM_CARE_KEY_SAVINGS_TAB,
      },
    ],
  },
  {
    id: 'profile',
    label: 'Profile',
    items: [
      {
        label: 'Profile',
        to: '/profile',
        component: EditPatient,
        description: 'Demographics, insurance, care team & equipment',
        personComponent: EditPerson,
      },
      {
        label: 'Device',
        to: '/meta',
        component: PatientDetailMeta,
        description: 'App and device information',
        feature: 'practice_settings',
        hideInPersonMode: true,
      },
    ],
  },
]

// Default landing sub-view when a category is selected with no item in the URL.
export const DEFAULT_ITEM_PATH = '/todos'
